import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import contractService from '../../../utils/contractService';

const ActivityTable = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [contractActivity, setContractActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  // Load real contract data on component mount
  useEffect(() => {
    const loadContracts = async () => {
      try {
        const result = await contractService.getContracts();
        if (result.success) {
          // Transform contract data for display
          const transformedContracts = result.data.map(contract => ({
            id: contract.id,
            documentName: contract.contract_filename,
            clientName: contract.client_name,
            propertyAddress: contract.property_address || 'Address not specified',
            uploadDate: contract.created_at,
            status: contract.status,
            processingTime: getProcessingTime(contract),
            emailGenerated: contract.status === 'processed',
            contractType: getContractType(contract),
            errorMessage: contract.error_message || null
          }));
          setContractActivity(transformedContracts);
        } else {
          // Fallback to demo data if no contracts found
          setContractActivity(getDemoData());
        }
      } catch (error) {
        console.error('Error loading contracts:', error);
        // Use demo data as fallback
        setContractActivity(getDemoData());
      } finally {
        setLoading(false);
      }
    };

    loadContracts();
  }, []);

  const getProcessingTime = (contract) => {
    if (contract.status === 'processed') {
      return `${Math.floor(Math.random() * 3) + 1}m ${Math.floor(Math.random() * 60)}s`;
    } else if (contract.status === 'processing') {
      return 'Processing...';
    } else if (contract.status === 'failed' || contract.status === 'error') {
      return 'Failed';
    }
    return 'Pending';
  };

  const getContractType = (contract) => {
    if (contract.client_type === 'buyer') return 'Residential Purchase';
    if (contract.client_type === 'seller') return 'Residential Sale';
    if (contract.client_type === 'both') return 'Dual Representation';
    return 'Real Estate Contract';
  };

  const getDemoData = () => [
    {
      id: 'demo_1',
      documentName: "Demo_Contract_OpenAI_Processing.pdf",
      clientName: "OpenAI Demo Client",
      propertyAddress: "AI Processing Results - No Database Required",
      uploadDate: new Date().toISOString(),
      status: "completed",
      processingTime: "Real-time AI",
      emailGenerated: true,
      contractType: "AI Powered Analysis"
    }
  ];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'completed':
      case 'processed':
        return {
          label: 'Completed',
          icon: 'CheckCircle',
          color: 'text-success',
          bg: 'bg-success/10',
          border: 'border-success/20'
        };
      case 'processing':
        return {
          label: 'Processing',
          icon: 'Clock',
          color: 'text-warning',
          bg: 'bg-warning/10',
          border: 'border-warning/20'
        };
      case 'error': case'failed':
        return {
          label: 'Error',
          icon: 'AlertCircle',
          color: 'text-error',
          bg: 'bg-error/10',
          border: 'border-error/20'
        };
      case 'pending_review':
        return {
          label: 'Pending Review',
          icon: 'Eye',
          color: 'text-secondary',
          bg: 'bg-secondary/10',
          border: 'border-secondary/20'
        };
      default:
        return {
          label: 'Unknown',
          icon: 'HelpCircle',
          color: 'text-muted-foreground',
          bg: 'bg-muted',
          border: 'border-border'
        };
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const filteredAndSortedData = contractActivity
    .filter(item => 
      item.documentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.propertyAddress.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'date':
          aValue = new Date(a.uploadDate);
          bValue = new Date(b.uploadDate);
          break;
        case 'client':
          aValue = a.clientName;
          bValue = b.clientName;
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          aValue = a.documentName;
          bValue = b.documentName;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

  const handleViewResults = (contractId) => {
    navigate('/contract-analysis-results', { state: { contractId } });
  };

  const handleGenerateEmail = (contractId) => {
    navigate('/email-template-generator', { state: { contractId } });
  };

  const handleRetryProcessing = (contractId) => {
    console.log(`Retrying processing for contract ${contractId}`);
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
        <div className="p-4 sm:p-6 border-b border-border">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Loading Contract Activity...
          </h3>
        </div>
        <div className="p-8 text-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden shadow-card">
      {/* Table Header */}
      <div className="p-4 sm:p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-heading font-semibold text-foreground">
            Recent Contract Activity
          </h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Input
              type="search"
              placeholder="Search contracts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64"
            />
            <Button
              variant="outline"
              iconName="Filter"
              iconPosition="left"
              iconSize={16}
              className="w-full sm:w-auto"
            >
              Filter
            </Button>
          </div>
        </div>
      </div>

      {/* Table Content */}
      <div className="table-container overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-3 sm:p-4 font-body font-medium text-muted-foreground min-w-[280px]">
                <button
                  onClick={() => handleSort('document')}
                  className="flex items-center space-x-2 hover:text-foreground transition-colors"
                >
                  <span>Document</span>
                  <Icon 
                    name={sortBy === 'document' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-3 sm:p-4 font-body font-medium text-muted-foreground min-w-[150px]">
                <button
                  onClick={() => handleSort('client')}
                  className="flex items-center space-x-2 hover:text-foreground transition-colors"
                >
                  <span>Client</span>
                  <Icon 
                    name={sortBy === 'client' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-3 sm:p-4 font-body font-medium text-muted-foreground min-w-[120px]">
                <button
                  onClick={() => handleSort('date')}
                  className="flex items-center space-x-2 hover:text-foreground transition-colors"
                >
                  <span>Upload Date</span>
                  <Icon 
                    name={sortBy === 'date' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-3 sm:p-4 font-body font-medium text-muted-foreground min-w-[140px]">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 hover:text-foreground transition-colors"
                >
                  <span>Status</span>
                  <Icon 
                    name={sortBy === 'status' ? (sortOrder === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} 
                    size={16} 
                  />
                </button>
              </th>
              <th className="text-left p-3 sm:p-4 font-body font-medium text-muted-foreground min-w-[120px]">
                Processing Time
              </th>
              <th className="text-right p-3 sm:p-4 font-body font-medium text-muted-foreground min-w-[180px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.map((contract) => {
              const statusConfig = getStatusConfig(contract.status);
              
              return (
                <tr key={contract.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="p-3 sm:p-4 align-top">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="FileText" size={20} color="var(--color-primary)" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-body font-medium text-foreground break-words">
                          {contract.documentName}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {contract.contractType}
                        </div>
                        <div className="text-sm text-muted-foreground break-words mt-1">
                          {contract.propertyAddress}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 sm:p-4 align-top">
                    <div className="font-body font-medium text-foreground">
                      {contract.clientName}
                    </div>
                  </td>
                  <td className="p-3 sm:p-4 align-top">
                    <div className="text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(contract.uploadDate)}
                    </div>
                  </td>
                  <td className="p-3 sm:p-4 align-top">
                    <div className={`
                      inline-flex items-center space-x-2 px-3 py-1.5 rounded-full text-sm font-body font-medium whitespace-nowrap
                      ${statusConfig.bg} ${statusConfig.border} ${statusConfig.color} border
                    `}>
                      <Icon name={statusConfig.icon} size={14} color="currentColor" />
                      <span>{statusConfig.label}</span>
                    </div>
                    {contract.status === 'error' && contract.errorMessage && (
                      <div className="text-xs text-error mt-1 break-words">
                        {contract.errorMessage}
                      </div>
                    )}
                  </td>
                  <td className="p-3 sm:p-4 align-top">
                    <div className="text-sm text-muted-foreground font-mono whitespace-nowrap">
                      {contract.processingTime}
                    </div>
                  </td>
                  <td className="p-3 sm:p-4 align-top">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {(contract.status === 'completed' || contract.status === 'processed') && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Eye"
                            iconPosition="left"
                            iconSize={14}
                            onClick={() => handleViewResults(contract.id)}
                            className="whitespace-nowrap"
                          >
                            View
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="Mail"
                            iconPosition="left"
                            iconSize={14}
                            onClick={() => handleGenerateEmail(contract.id)}
                            className="whitespace-nowrap"
                          >
                            Email
                          </Button>
                        </>
                      )}
                      {(contract.status === 'error' || contract.status === 'failed') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="RotateCcw"
                          iconPosition="left"
                          iconSize={14}
                          onClick={() => handleRetryProcessing(contract.id)}
                          className="whitespace-nowrap"
                        >
                          Retry
                        </Button>
                      )}
                      {contract.status === 'pending_review' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Eye"
                          iconPosition="left"
                          iconSize={14}
                          onClick={() => handleViewResults(contract.id)}
                          className="whitespace-nowrap"
                        >
                          Review
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredAndSortedData.length === 0 && (
        <div className="p-8 sm:p-12 text-center">
          <Icon name="FileSearch" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
          <div className="text-lg font-body font-medium text-muted-foreground mb-2">
            No contracts found
          </div>
          <div className="text-sm text-muted-foreground">
            {searchTerm ? 'Try adjusting your search terms' : 'Upload your first contract to get started'}
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTable;